import { IcCopyPaste, IcEdit, IcPlus as IcPlus24, IcTrash } from '@assets/icons/24x24';
import { IcPlus } from '@assets/icons/32x32';
import { ButtonGroup, For, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import CockpitDialog, { CockpitDialogProps } from '@components/cockpit/CockpitDialog';
import { CockpitListLayout } from '@components/cockpit/layouts/CockpitListLayout';
import CockpitRow from '@components/cockpit/layouts/CockpitRow';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole, selectAllProfiles } from '@redux/authent/authent.selectors';
import { useCreateProfileMutation, useDeleteProfileMutation } from '@services/authent/profile.api';
import { generateDuplicateName, isRestrictedProfile } from '@utils/authent/profiles.utils';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Profile } from '@/types/authent/groups.types';
import { RoleEnum } from '@/types/authent/roles.types';

import { UsersManagementItemTypeEnum, useUsersManagementContext } from '../UsersManagement';

type ProfilesDialogProps = {
  selectedProfileName?: string;
} & Omit<CockpitDialogProps, 'children'>;

function ProfilesDialog({ selectedProfileName, open, isLoading, onCancel, onConfirm }: ProfilesDialogProps) {
  return (
    <CockpitDialog open={open} isLoading={isLoading} onCancel={onCancel} onConfirm={onConfirm}>
      <Text color="neutral.100" width="100%">
        <FormattedMessage
          id="components.usersManagement.profiles.popup.descriptionDelete"
          values={{ name: selectedProfileName }}
        />
      </Text>
      <Text color="neutral.100" width="100%">
        <FormattedMessage id="components.usersManagement.profiles.popup.descriptionDeleteWarning" />
      </Text>
      <Text color="neutral.100" width="100%">
        <FormattedMessage id="components.usersManagement.profiles.popup.descriptionDeleteLogout" />
      </Text>
    </CockpitDialog>
  );
}

type ProfilesActionsProps = {
  canCreateProfile: boolean;
  profileLength: number;
  onCreate: () => void;
};

function ProfilesActions({ canCreateProfile, profileLength, onCreate }: Readonly<ProfilesActionsProps>) {
  return (
    <>
      <Text color="neutral.500">
        <FormattedMessage id="components.usersManagement.groups.result" values={{ count: profileLength }} />
      </Text>
      {canCreateProfile && (
        <IconButton aria-label="Add" variant="transparent" boxSize="56px" marginLeft="auto" onClick={onCreate}>
          <Icon as={IcPlus24} boxSize="24px" />
        </IconButton>
      )}
    </>
  );
}

export default function Profiles() {
  const { formatMessage } = useIntl();

  const profiles = useAppSelector(selectAllProfiles);
  const canEditProfile = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_PROFILE));
  const canCreateProfile = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_PROFILE));
  const canDeleteProfile = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_PROFILE));
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [deleteProfile, { isLoading }] = useDeleteProfileMutation();
  const [createProfile] = useCreateProfileMutation();
  const [loadingCopy, setLoadingCopy] = useState<number | null>(null);

  const { setEditedItem } = useUsersManagementContext();

  return (
    <CockpitListLayout
      dialog={
        <ProfilesDialog
          open={selectedProfile !== null}
          isLoading={isLoading}
          onCancel={() => setSelectedProfile(null)}
          onConfirm={() => {
            if (!selectedProfile) {
              return;
            }
            deleteProfile(selectedProfile.id)
              .unwrap()
              .then(() => {
                createToast(
                  formatMessage(
                    { id: 'components.usersManagement.profiles.message.delete' },
                    { name: selectedProfile.name },
                  ),
                  ToastStatusEnum.SUCCESS,
                );
                setSelectedProfile(null);
              });
          }}
          selectedProfileName={selectedProfile?.name}
        />
      }
      actions={
        <ProfilesActions
          profileLength={profiles.length}
          canCreateProfile={canCreateProfile}
          onCreate={() => setEditedItem({ type: UsersManagementItemTypeEnum.PROFILE, id: null })}
        />
      }
    >
      <For
        each={profiles.toSorted((a, b) => a.name.localeCompare(b.name))}
        fallback={
          canCreateProfile ? (
            <VStack
              padding={4}
              gap={2}
              marginY="auto"
              className="group"
              border="2px solid"
              cursor="pointer"
              borderColor="neutral.600"
              _hover={{ borderColor: 'skyBlue', backgroundColor: 'neutral.800' }}
              onClick={() => setEditedItem({ type: UsersManagementItemTypeEnum.PROFILE, id: null })}
            >
              <Icon as={IcPlus} boxSize="32px" color="neutral.300" _groupHover={{ color: 'skyBlue' }} />
              <Text>
                <FormattedMessage id="components.usersManagement.profiles.createFirst" />
              </Text>
            </VStack>
          ) : undefined
        }
      >
        {(profile, index) => {
          const isRestricted = isRestrictedProfile(profile);
          return (
            <CockpitRow index={index} listLength={profiles.length} key={index}>
              <Text fontWeight="medium">{profile.name}</Text>
              <ButtonGroup marginLeft="auto" attached height="100%">
                {canEditProfile && !isRestricted && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="edit"
                    onClick={() => setEditedItem({ type: UsersManagementItemTypeEnum.PROFILE, id: profile.id })}
                  >
                    <Icon as={IcEdit} boxSize="24px" />
                  </IconButton>
                )}
                {canCreateProfile && !isRestricted && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="duplicate"
                    loading={loadingCopy === profile.id}
                    onClick={() => {
                      setLoadingCopy(profile.id);
                      createProfile({
                        ...profile,
                        name: generateDuplicateName(profile.name, new Set(profiles.map((e) => e.name))),
                        id: undefined,
                      })
                        .unwrap()
                        .then(() => {
                          setLoadingCopy(null);
                          createToast(
                            formatMessage(
                              { id: 'components.usersManagement.profiles.message.copy' },
                              { name: profile.name },
                            ),
                            ToastStatusEnum.SUCCESS,
                          );
                        });
                    }}
                  >
                    <Icon as={IcCopyPaste} boxSize="24px" />
                  </IconButton>
                )}
                {canDeleteProfile && !isRestricted && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="delete"
                    onClick={() => setSelectedProfile(profile)}
                  >
                    <Icon as={IcTrash} boxSize="24px" />
                  </IconButton>
                )}
              </ButtonGroup>
            </CockpitRow>
          );
        }}
      </For>
    </CockpitListLayout>
  );
}
